import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert,
    AlertIcon, HStack, IconButton, useToast, Badge, Tag
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import * as compoundAlertRuleService from '../../services/compoundAlertRuleService';

const CompoundAlertRulesTable = ({ onEdit, onRefresh }) => {
    const [rules, setRules] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const toast = useToast();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await compoundAlertRuleService.fetchCompoundAlertRules();
            setRules(data);
        } catch (err) {
            setError('Failed to fetch compound alert rules.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData, onRefresh]);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this compound rule?')) {
            try {
                await compoundAlertRuleService.deleteCompoundAlertRule(id);
                toast({ title: 'Compound rule deleted.', status: 'success' });
                fetchData(); // Refresh the list
            } catch (err) {
                toast({ title: 'Error deleting rule.', description: err.message, status: 'error' });
            }
        }
    };
    
    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'blue';
            default: return 'gray';
        }
    };

    if (isLoading) return <Spinner />;
    if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;

    return (
        <TableContainer>
            <Table variant="simple">
                <Thead>
                    <Tr>
                        <Th>Name</Th>
                        <Th>Description</Th>
                        <Th>Severity</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {rules.map((rule) => (
                        <Tr key={rule.id}>
                            <Td>{rule.name}</Td>
                            <Td maxW="400px" whiteSpace="normal">{rule.description}</Td>
                            <Td>
                                <Tag colorScheme={getSeverityColor(rule.severity)}>
                                    {rule.severity}
                                </Tag>
                            </Td>
                            <Td>
                                <Badge colorScheme={rule.is_enabled ? 'green' : 'gray'}>
                                    {rule.is_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </Td>
                            <Td>
                                <HStack spacing={2}>
                                    <IconButton
                                        icon={<EditIcon />}
                                        aria-label="Edit Rule"
                                        size="sm"
                                        onClick={() => onEdit(rule.id)}
                                    />
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        aria-label="Delete Rule"
                                        size="sm"
                                        colorScheme="red"
                                        onClick={() => handleDelete(rule.id)}
                                    />
                                </HStack>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
};

export default CompoundAlertRulesTable;